import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core'
import { ApiImage } from '../../../core/models/product/image'
import { SimpleProduct } from '../../../core/models/product'
import { AddCartEvent, ApiOrderline } from '../../../cart/models/cart-models'
import { ABSOLUTE_QUANT, RELATIVE_QUANT, ThemeConfig } from '../../../core/helpers/theme-config';

const quantityType = ThemeConfig.get<string>('catalog', 'quantity-type')
const hasDetails = ThemeConfig.get<boolean>('catalog', 'has-details')

export type ModelType = {
  line: ApiOrderline,
  currency: string
  editable: boolean
}

@Component({
  selector: 'app-cart-table-line',
  templateUrl: './template.html',
  styleUrls: ['./styles.scss'],
  host: { "class": "tr" },
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CartTableLineComponent {

  @Input() line: ApiOrderline
  @Input() editable: boolean
  @Input() currency: string
  @Input() showDeliveryColumn: boolean
  @Input() quantityPicker: string
  @Input() inBackorder: boolean
  @Input() showDiscount: boolean
  @Input() showFacets: string[]

  @Output() onLineChange = new EventEmitter<AddCartEvent>()
  @Output() onLineDelete = new EventEmitter<string>()
  @Output() onDetails = new EventEmitter<string>()

  get product(): SimpleProduct {
    return this.line.product
  }

  get imageUrl(): string {
    return (this.product.assets.imgs
      .find(img => img.type === 'detail') || {} as ApiImage )
      .src
  }

  get name(): string {
    return this.product.name
  }

  get productNumber(): string {
    return this.product.code
  }

  get productDisplayCode(): string {
    return this.product.displayCode
  }

  get hasDetails(): boolean {
    return hasDetails
  }

  get stepping(): number {
    return quantityType === ABSOLUTE_QUANT
      ? this.line.stepping
      : 1
  }

  get actualStepping(): number {
    return this.line.stepping || 1
  }

  get minQuantity(): number {
    return quantityType === RELATIVE_QUANT
      ? this.line.minQuantity / this.line.stepping
      : this.line.minQuantity
  }

  get absoluteQuantity(): number {
    return this.line.quantity
  }

  get relativeQuantity(): number {
    return this.line.quantity / this.line.stepping
  }

  get unitPrice(): number {
    return this.line.itemprice
  }

  get normalUnitPrice() {
    return this.line.origitemprice
  }

  get hasDiscount(): boolean {
    return this.discount > 0
  }

  get discount(): number {
    return this.line.discount
  }

  get linePrice(): number {
    return this.line.lineprice
  }

  get normalLinePrice(): number {
    return this.line.origlineprice
  }

  get stockMessage(): string {
    return 'stock.code-' + this.line.delivery.expected.code
  }

  get stockMessageClass(): string {
    return 'stockmessage-code-' + this.line.delivery.expected.code
  }

  get stockIconClass(): string {
    return this.stockIconClass;
  }

  get week(): string {
    return this.line.delivery.expected.value
  }

  get calculatedQuantity(): number {
    return quantityType === RELATIVE_QUANT
      ? this.relativeQuantity
      : this.absoluteQuantity
  }

  get displayQuantity(): number {
    return this.quantityPickerRelative
      ? this.relativeQuantity
      : this.absoluteQuantity
  }

  get showQuantityPickerAnimated(): boolean {
    return (this.quantityPicker && this.quantityPicker.toLowerCase() === 'animated')
  }

  get quantityPickerRelative(): boolean {
    return (this.quantityPicker && this.quantityPicker.toLowerCase() === 'relative')
  }

  get hasFacetsToDisplay(): boolean {
    return this.showFacets && this.showFacets.length > 0
  }

  get facetsWithValues() {
    if (this.product.facetsWithValues && this.product.facetsWithValues.length > 0) {
      const applicableFacetWithValues = this.product.facetsWithValues
        .filter(facetWithValues => this.showFacets.indexOf(facetWithValues.facetgroupcode) > -1)

      return applicableFacetWithValues
    }
  }

  openDetails(product: string) {
    this.onDetails.emit(product)
  }

  onQuantityChanged(event: AddCartEvent) {
    this.onLineChange.emit({
      quantity: event.quantity,
      product: this.line.product._id,
      includeExtraCosts: true
    })
  }

  setPlaceholder(event) {
    event.target.src = 'https://via.placeholder.com/125x125/f7f7f7?text=No+image'
  }

  removeLine() {
    this.onLineDelete.emit(this.line._id)
  }
}
